import Utils from "pages/common/lib/grabyo-util";
import {
    isProdEnvironment,
    SENTRY_REPORT_URI,
    apiDomain
} from "util/env-utils";
import sanitizeHtml from "sanitize-html";
import Constants from "const/constants";
import type { ServerResponse } from "http";
import { sha256 } from "util/hash-utils";

const grabyoDomain = `https://*.${apiDomain}`;

const GRABYO_SOURCES = [
    `'self'`,
    `https://grabyo.com`,
    grabyoDomain,
    Constants.CDN_URL,
    `https://dwo3ckksxlb0v.cloudfront.net`
];

const HEAP_SOURCES = [
    `https://heapanalytics.com`,
    `https://cdn.heapanalytics.com`
];

const INTERCOM_SOURCES = [
    `https://widget.intercom.io`,
    `https://js.intercomcdn.com`
];

const SecurityHeaders = {
    CSP: `Content-Security-Policy`,
    CSPReport: `Content-Security-Policy-Report-Only`
};

export const LIST_OF_BASE_URLS_THAT_SHOULD_HAVE_THE_CSP_ENABLED = [`/login`];

function createBaselineCSPKeyValuePairs() {
    return {
        "frame-ancestors": GRABYO_SOURCES,
        "report-uri": [SENTRY_REPORT_URI]
    };
}

export function generateContentSecurityPolicy(
    nonceVal: string,
    extendBaseline = false
) {
    const nonce = `'nonce-${nonceVal}'`;
    const policy = {
        ...(extendBaseline ? createBaselineCSPKeyValuePairs() : {}),
        "script-src": [
            ...(!isProdEnvironment ? [`'unsafe-inline'`] : []),
            ...GRABYO_SOURCES,
            ...HEAP_SOURCES,
            ...INTERCOM_SOURCES,
            `https://www.google-analytics.com`,
            `https://static.filestackapi.com`,
            `https://www.googletagmanager.com`,
            `'unsafe-eval'`,
            nonce
        ],
        "script-src-elem": [
            ...(!isProdEnvironment
                ? [`'unsafe-inline'`, `'unsafe-eval'`]
                : [nonce]),
            ...GRABYO_SOURCES,
            ...HEAP_SOURCES,
            ...INTERCOM_SOURCES,
            `https://*.google-analytics.com`,
            `https://www.googletagmanager.com`,
            `https://static.filestackapi.com`,
            `'unsafe-eval'`
        ],
        "report-uri": [SENTRY_REPORT_URI]
    };

    return stringifyCSP(policy);
}

export function generateMinumumContentSecurityPolicy() {
    return stringifyCSP(createBaselineCSPKeyValuePairs());
}

/**
 * @summary Create CSP header from JS object
 * @param policy
 */
export function stringifyCSP(policy: Record<string, string[]>) {
    let header = ``;
    for (const directive in policy) {
        header += `${directive} ${policy[directive].join(` `)}; `;
    }

    return header;
}

export const CSPSetting = {
    Baseline: 0,
    Test: 1,
    Experimental: 2
};

/**
 * @summary Applies a Content Security Policy to the page response
 *
 * @param res - The page response object
 * @param nonce - Used for the CSP to match allowed scripts
 * @param [cspSetting=CSPSetting.Baseline] - Just the standard CSP by default but allows you to try experimental CSP settings in report mode only or enforce them.
 *
 */
export function applyCSP(
    res: ServerResponse,
    nonce: string,
    cspSetting = CSPSetting.Baseline
) {
    /**
     * There doesn't seem to be an elegant way of
     * passing data between the _app.tsx getInitialProps
     * and the _document.tsx getInitialProps, so a header
     * is added here, then removed in _document.tsx
     *
     */
    res.appendHeader(`nonce`, nonce);

    switch (cspSetting) {
        case CSPSetting.Experimental: {
            res.setHeader(
                SecurityHeaders.CSP,
                generateContentSecurityPolicy(nonce, true)
            );
            break;
        }
        case CSPSetting.Test: {
            res.setHeader(
                SecurityHeaders.CSPReport,
                generateContentSecurityPolicy(nonce)
            );
            res.setHeader(
                SecurityHeaders.CSP,
                generateMinumumContentSecurityPolicy()
            );
            break;
        }
        case CSPSetting.Baseline:
        default: {
            res.setHeader(
                SecurityHeaders.CSP,
                generateMinumumContentSecurityPolicy()
            );
        }
    }
}

/**
 * @summary Valid that a URL is safe to redirect a user to
 *
 * @param url - The URL to sanitize
 * @returns A safe path for redirection
 */
export const sanitizeRedirectDestination = (url = ``) => {
    try {
        // Test if it's a formed URL
        const parsedUrl = new URL(url);
        // Exclude any other origins or javascript: urls
        if (parsedUrl.origin === self.origin) {
            const queryString =
                //@ts-ignore: length does not exist
                parsedUrl.searchParams.length > 0
                    ? `?${parsedUrl.searchParams}`
                    : ``;
            return parsedUrl.pathname + queryString;
        }
    } catch (e) {
        // Path is internal
        return url;
    }
    // Fallback to index
    return `/`;
};

/**
 * Sanitize Social Mentions for rendering.
 * If the description contains a mention,
 * we dynamically insert HTML to display a tooltip.
 * We need to support a known set of HTML
 * and filter anything else to prevent XSS.
 *
 * @param str - The string to sanitize
 */
export const sanitizeMentionsString = (str: string) =>
    sanitizeHtml(str, {
        allowedTags: [`ul`, `li`, `img`, `span`, `a`],
        allowedAttributes: {
            a: [`href`, `target`],
            img: [`src`, `width`, `height`],
            span: [
                `class`,
                `data-html`,
                `data-toggle`,
                `data-delay`,
                `data-original-title`
            ],
            li: [`class`]
        },
        allowedSchemes: [`https`]
    });
